<script setup lang="ts">
import { useAuthStore } from '@/stores/auth'
import { useMeStore } from '@/stores/me'
import { brokerIsStroobants } from '@/utils/brokers'
import { RequestStatus } from '@/utils/restStore'
import { getCurrentInstance, watch } from 'vue'
import { onMounted } from 'vue'
import { RouterView } from 'vue-router'
import { useRoute, useRouter } from 'vue-router'

import B2bOnBoarding from '@/components/home/organisms/B2bOnBoarding.vue'
import B2cOnBoarding from '@/components/home/organisms/B2cOnBoarding.vue'

import ProfileSelect from './components/generic/atoms/ProfileSelect.vue'
import ProfilesModal from './components/generic/organisms/ProfilesModal.vue'
import AppStructure from './components/generic/templates/AppStructure.vue'
import { RouteNames, SubDomain } from './router'
import { ProfileType } from './types/me'
import { initializeCio } from './utils/script_integrations'

const authStore = useAuthStore()
const meStore = useMeStore()

const PROFILE_SWITCH_REDIRECTS: Partial<Record<RouteNames, RouteNames>> = {
  [RouteNames.ContractDetails]: RouteNames.Contracts,
  [RouteNames.Covers]: RouteNames.Contracts,
  [RouteNames.Invoices]: RouteNames.Contracts,
  [RouteNames.ContractDocuments]: RouteNames.Contracts,
  [RouteNames.InsuranceCertificates]: RouteNames.Contracts,
  [RouteNames.LifeProcedure]: RouteNames.Contracts,
  [RouteNames.ClaimDetails]: RouteNames.Claims,
  [RouteNames.ClaimDeclaration]: RouteNames.Claims,
  [RouteNames.OnlineSignature]: RouteNames.AccountSignatures
}

const closeB2cOnbardingModal = () => {
  meStore.postOnboarded(ProfileType.B2C)
}

const closeB2bOnbardingModal = () => {
  meStore.postOnboarded(ProfileType.B2B)
}

const route = useRoute()
const router = useRouter()

const instance = getCurrentInstance()
let cioAnalytics: any | null = null
if (instance) {
  initializeCio(instance).then((analytics) => (cioAnalytics = analytics))
}

const identifyCio = async () => {
  if (!cioAnalytics) return

  if (meStore.me.status !== RequestStatus.RequestLoaded) {
    await meStore.fetchMe().catch(() => {})
  }
  if (meStore.me.status === RequestStatus.RequestLoaded) {
    cioAnalytics.identify(meStore.me.data.uuid, {
      id: meStore.me.data.uuid, // ID interne Yago (ex: UUID client)
      email: meStore.me.data.email // Email principal
    })
  }
}

const setToken = async () => {
  await meStore.fetchToken()
}

watch(
  () => authStore.isAuthenticated,
  async (value) => {
    if (value) meStore.fetchProfiles()
    identifyCio()
    if (!brokerIsStroobants()) {
      setToken()
    }
  }
)

watch(
  () => meStore.selectedCompanyId ?? meStore.selectedClientId,
  (_newKey, oldKey) => {
    if (!oldKey) return
    if (!route.name || typeof route.name !== 'string') return
    const redirectTo =
      route.name === RouteNames.Company && !meStore.selectedCompanyId
        ? RouteNames.Account
        : PROFILE_SWITCH_REDIRECTS[route.name as RouteNames]
    if (redirectTo) router.push({ name: redirectTo, params: { ...route.params } })
  }
)

function setFavicon(iconUrl: string, title: string, manifestUrl: string) {
  let link: HTMLLinkElement | null = document.querySelector("link[rel~='icon']")
  if (!link) {
    link = document.createElement('link')
    link.rel = 'icon'
    document.head.appendChild(link)
  }
  link.href = iconUrl

  let metaOgImage: HTMLMetaElement | null = document.querySelector("meta[property='og:image']")
  if (!metaOgImage) {
    metaOgImage = document.createElement('meta')
    metaOgImage.setAttribute('property', 'og:image')
    document.head.appendChild(metaOgImage)
  }
  metaOgImage.setAttribute('content', iconUrl)

  let appleTouchIcon: HTMLLinkElement | null = document.querySelector("link[rel='apple-touch-icon']")
  if (!appleTouchIcon) {
    appleTouchIcon = document.createElement('link')
    appleTouchIcon.rel = 'apple-touch-icon'
    document.head.appendChild(appleTouchIcon)
  }
  appleTouchIcon.href = iconUrl

  let manifest: HTMLLinkElement | null = document.querySelector("link[rel='manifest']")
  if (!manifest) {
    manifest = document.createElement('link')
    manifest.rel = 'manifest'
    document.head.appendChild(manifest)
  }
  manifest.href = manifestUrl

  document.title = title
}

onMounted(async () => {
  // Wait for the router to be ready before doing any other action
  await router.isReady()

  if (brokerIsStroobants()) setFavicon('/stroobants-512x512.png', 'My Yago for Stroobants', '/stroobants-manifest.json')
  else setFavicon('/yago-256x256.png', 'My Yago', '/manifest.json')

  // document.querySelectorAll("link[rel~='icon']").forEach((link) => link.remove())
  if (meStore.me.status === RequestStatus.NoRequest) await meStore.fetchMe()
  if (meStore.stroobantsClient)
    router.push({
      name: route.name ? route.name : 'home',
      params: { ...route.params, subdomain: SubDomain.Stroobants }
    })
})
</script>

<template>
  <ProfilesModal
    v-if="
      meStore.me.status === RequestStatus.RequestLoaded && !meStore.selectedClientId && !meStore.selectedCompanyId
    " />

  <B2cOnBoarding :close-modal="closeB2cOnbardingModal" v-if="meStore.showB2cOnboarding" />
  <B2bOnBoarding :close-modal="closeB2bOnbardingModal" v-if="meStore.showB2bOnboarding" />
  <AppStructure>
    <ProfileSelect class="lg:hidden" />
    <RouterView :key="meStore.selectedCompanyId ?? meStore.selectedClientId ?? 'none'" />
  </AppStructure>
</template>
